import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Plus, Trash2, ShoppingBag, X } from "lucide-react";

const num = (s) => { const m = String(s || "").replace(",", ".").match(/\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
const pct = (price, old) => { const a = num(price), b = num(old); return a != null && b && b > a ? Math.round((1 - a / b) * 100) : null; };
const cash = (s) => (/^\s*\d+([.,]\d+)?\s*$/.test(String(s)) ? `${String(s).trim()} €` : s);

export const wheelEligible = (cat, sub) => ["dyqan", "sherbime"].includes(cat) || (cat === "reklame" && /dyqan|shërbim|sherbim/i.test(sub || ""));

export function ProductWheel({ items, title }) {
  const [view, setView] = useState(null);
  const list = (items || []).filter((p) => p.name || p.image);
  if (!list.length) return null;
  return (
    <>
      <WheelBody list={list} title={title} onOpen={setView} paused={view !== null} />
      {view !== null && createPortal(
        <div className="fixed inset-0 z-[2000] bg-black flex flex-col justify-center" data-testid="product-wheel-viewer">
          <button onClick={() => setView(null)} data-testid="product-wheel-close" aria-label="Mbyll" className="absolute top-4 right-4 z-30 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
          <WheelBody big list={list} title={title} start={view} />
        </div>,
        document.body
      )}
    </>
  );
}

function WheelBody({ list, title, big = false, start = 0, onOpen, paused = false }) {
  const n = list.length;
  const [idx, setIdx] = useState(start);
  const hold = useRef(0);
  const tx = useRef(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  useEffect(() => {
    if (n < 2) return undefined;
    const id = setInterval(() => { if (!pausedRef.current && Date.now() > hold.current && !document.hidden) setIdx((i) => (i + 1) % n); }, 3000);
    return () => clearInterval(id);
  }, [n]);
  const go = (d) => { hold.current = Date.now() + 6000; setIdx((i) => (i + d + n) % n); };
  const cur = list[idx];
  const isNum = (s) => /^\s*[€$£]?\s*\d+([.,]\d+)?\s*(€|kr|sek|chf|\$|£|eur|lek|den)?\s*$/i.test(String(s || ""));
  const price = cur.price || (isNum(cur.desc) ? cur.desc : "");
  const desc = price === cur.desc ? "" : cur.desc;
  const off = (i) => { let o = i - idx; if (o > n / 2) o -= n; if (o < -n / 2) o += n; return o; };
  return (
    <section className={big ? "w-full" : "px-4 mt-6"} data-testid={big ? "product-wheel-big" : "product-wheel"}>
      {title && <h2 className={`font-extrabold text-lg mb-2 ${big ? "text-white text-center" : "text-slate-900"}`}>{title}</h2>}
      <div className={`relative select-none overflow-hidden ${big ? "h-[58vh]" : "h-60"}`} style={{ touchAction: "pan-y" }}
        onTouchStart={(e) => { tx.current = e.touches[0].clientX; hold.current = Date.now() + 6000; }}
        onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (tx.current ?? 0); if (Math.abs(dx) > 35) go(dx < 0 ? 1 : -1); }}>
        {list.map((p, i) => {
          const o = off(i), a = Math.abs(o);
          if (a > 2) return null;
          return (
            <button key={i} type="button" data-testid={`wheel-item-${i}`} onClick={() => (o !== 0 ? go(o) : onOpen && onOpen(idx))}
              className="absolute top-1/2 left-1/2 h-[92%] aspect-[3/4] overflow-hidden bg-white rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,.15)] p-2 flex items-center justify-center transition-[transform,opacity] duration-500 ease-out"
              style={{ transform: `translate(-50%,-50%) translateX(${o * 62}%) scale(${1 - a * 0.22}) perspective(600px) rotateY(${-o * 18}deg)`, zIndex: 10 - a, opacity: a === 2 ? 0.75 : 1 }}>
              {p.image ? <img src={p.image} alt={p.name} draggable={false} className="max-w-full max-h-full object-contain" style={{ transform: `scale(${p.img_scale || 1})` }} /> : <ShoppingBag className="w-10 h-10 text-slate-300" />}
              {o === 0 && pct(p.price, p.old_price) && <span className="absolute top-2 left-2 bg-red-600 text-white text-xs font-black px-2 py-0.5 rounded-full">-{pct(p.price, p.old_price)}%</span>}
            </button>
          );
        })}
      </div>
      <div className="text-center mt-1" data-testid="wheel-current">
        <div className={`font-extrabold leading-tight ${big ? "text-white text-2xl" : "text-slate-900 text-xl"}`} data-testid="wheel-name">{cur.name}</div>
        {desc && <div className={`text-xs mt-0.5 ${big ? "text-white/70" : "text-slate-500"}`}>{desc}</div>}
        <div className="flex items-center justify-center gap-2 mt-1.5">
          {cur.old_price && <span className={`text-base font-bold line-through decoration-2 decoration-red-500 ${big ? "text-white" : "text-slate-800"}`} data-testid="wheel-old-price">{cash(cur.old_price)}</span>}
          {price && (
            <span data-testid="wheel-price" className="relative inline-flex items-center pl-5 pr-3 py-1 rounded-md bg-emerald-600 text-white font-extrabold text-base shadow-sm" style={{ clipPath: "polygon(10px 0,100% 0,100% 100%,10px 100%,0 50%)" }}>
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white" />
              {cash(price)}
            </span>
          )}
        </div>
      </div>
      {n > 1 && <div className="flex justify-center gap-1.5 mt-3">{list.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-[width,background-color] ${i === idx ? "w-5 bg-emerald-500" : "w-1.5 bg-slate-300"}`} />)}</div>}
    </section>
  );
}

// Editor used by Shërbime (title + products)
export function ProductEditor({ title, setTitle, items, setItems, upload }) {
  const set = (i, patch) => setItems((p) => p.map((u, x) => (x === i ? { ...u, ...patch } : u)));
  return (
    <div className="space-y-3" data-testid="product-editor">
      <input data-testid="products-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titulli (p.sh. Ofertat e javës)" className="w-full border border-slate-200 rounded-xl px-3 h-11 text-sm font-bold outline-none" />
      {items.map((p, i) => (
        <div key={i} className="bg-slate-50 rounded-xl border border-slate-200 p-2.5 flex gap-2">
          <label className="w-16 h-16 shrink-0 rounded-lg border border-dashed border-slate-300 flex items-center justify-center overflow-hidden cursor-pointer bg-white">
            {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" style={{ transform: `scale(${p.img_scale || 1})` }} /> : <Camera className="w-5 h-5 text-slate-400" />}
            <input type="file" accept="image/*" className="hidden" data-testid={`pe-img-${i}`} onChange={async (e) => { const f = e.target.files[0]; if (f) set(i, { image: await upload(f) }); }} />
          </label>
          <div className="flex-1 min-w-0 space-y-1.5">
            <input data-testid={`pe-name-${i}`} value={p.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Emri i produktit / shërbimit" className="w-full border border-slate-200 rounded-lg px-2 h-9 text-sm outline-none" />
            <PriceInputs p={p} onChange={(patch) => set(i, patch)} id={i} />
            <ImgScale p={p} id={i} onChange={(patch) => set(i, patch)} />
          </div>
          <button type="button" onClick={() => setItems((x) => x.filter((_, j) => j !== i))} data-testid={`pe-del-${i}`} className="text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" data-testid="pe-add" onClick={() => setItems((p) => [...p, { name: "", price: "", old_price: "", image: "" }])} className="w-full flex items-center justify-center gap-1 bg-emerald-50 text-emerald-700 font-semibold rounded-xl py-3"><Plus className="w-4 h-4" />Shto produkt</button>
    </div>
  );
}

export function ImgScale({ p, onChange, id }) {
  if (!p.image) return null;
  const s = p.img_scale || 1;
  return (
    <label className="flex items-center gap-2 text-[11px] font-semibold text-slate-500">
      Madhësia e fotos
      <input type="range" min="0.5" max="2.5" step="0.05" value={s} data-testid={`img-scale-${id}`} onChange={(e) => onChange({ img_scale: parseFloat(e.target.value) })} className="flex-1 accent-emerald-500" />
      <span className="w-9 text-right">{Math.round(s * 100)}%</span>
    </label>
  );
}

export function PriceInputs({ p, onChange, id }) {
  return (
    <div className="flex gap-1.5">
      <input data-testid={`price-${id}`} value={p.price || ""} onChange={(e) => onChange({ price: e.target.value })} placeholder="Çmimi (p.sh. 29 kr)" className="flex-1 min-w-0 border border-emerald-300 bg-emerald-50/40 rounded-lg px-2 h-9 text-sm font-semibold outline-none" />
      <input data-testid={`old-price-${id}`} value={p.old_price || ""} onChange={(e) => onChange({ old_price: e.target.value })} placeholder="Para zbritjes" className="flex-1 min-w-0 border border-slate-200 rounded-lg px-2 h-9 text-sm outline-none" />
    </div>
  );
}

export default ProductWheel;
